import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import './SurveyPage.css';

const SurveyPage = ({ apiUrl, wsUrl }) => {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  // State
  const [isConnected, setIsConnected] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [messages, setMessages] = useState([]);
  const [currentTranscript, setCurrentTranscript] = useState('');
  const [progress, setProgress] = useState({ answered: 0, total: 0, core_answered: 0, core_total: 27, adaptive_count: 0 });
  const [phase, setPhase] = useState('core'); // core | adaptive | finished
  const [canFinish, setCanFinish] = useState(false);
  const [showCheckpoint, setShowCheckpoint] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState('');
  const [micPermission, setMicPermission] = useState('unknown'); // 'unknown', 'granted', 'denied'
  const [voiceId, setVoiceId] = useState(null); // ElevenLabs voice ID for TTS (null = use default)
  const [hasClonedVoice, setHasClonedVoice] = useState(false);
  const [isPreparingVoice, setIsPreparingVoice] = useState(false); // Loading state while TTS prepares
  
  // Session resume state
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [savedSessionInfo, setSavedSessionInfo] = useState(null);
  const [waitingForChoice, setWaitingForChoice] = useState(false);
  
  // Refs
  const wsRef = useRef(null);
  const recognitionRef = useRef(null);
  const audioContextRef = useRef(null);
  const currentAudioRef = useRef(null);
  const messagesEndRef = useRef(null);
  const isListeningRef = useRef(false); // Track listening state for recognition restart
  const voiceIdRef = useRef(null); // Sync ref for voice ID (avoids React state async issues)
  const isLeavingRef = useRef(false); // Prevent new audio when leaving page
  
  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Cleanup on unmount - stop all audio
  useEffect(() => {
    return () => {
      // Mark that we're leaving to prevent new audio
      isLeavingRef.current = true;
      
      // Stop any playing audio
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current.src = ''; // Clear source to fully stop
        currentAudioRef.current = null;
      }
      // Clear audio queue and revoke any pending URLs
      if (audioQueueRef.current) {
        audioQueueRef.current.forEach(url => URL.revokeObjectURL(url));
        audioQueueRef.current = [];
      }
      isPlayingQueueRef.current = false;
      // Stop speech synthesis
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      // Close WebSocket
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close();
      }
      // Stop speech recognition
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);
  
  // Check microphone permission on mount
  useEffect(() => {
    const checkMicPermission = async () => {
      try {
        const result = await navigator.permissions.query({ name: 'microphone' });
        setMicPermission(result.state);
        result.addEventListener('change', () => {
          setMicPermission(result.state);
        });
      } catch (e) {
        // Permissions API not supported, will check when requesting mic
        console.log('Permissions API not supported');
      }
    };
    checkMicPermission();
  }, []);
  
  // Audio queue for chunked TTS playback
  const audioQueueRef = useRef([]);
  const isPlayingQueueRef = useRef(false);
  
  // Note: Voice ID is now obtained from WebSocket response (more reliable timing)
  // This useEffect is kept as a fallback but WebSocket takes priority
  useEffect(() => {
    const loadProfile = async () => {
      if (!profileId || !token) return;
      
      try {
        const response = await axios.get(`${apiUrl}/personalization/profiles/${profileId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.elevenlabs_voice_id) {
          // Only set if not already set by WebSocket
          setVoiceId(prev => prev || response.data.elevenlabs_voice_id);
          setHasClonedVoice(prev => prev || true);
          console.log('📋 Profile loaded, voice available:', response.data.elevenlabs_voice_id);
        }
        // Don't log "no voice" here - WebSocket will provide the authoritative answer
      } catch (err) {
        // Silently fail - WebSocket will handle voice info
        console.log('Profile API fallback not available, using WebSocket for voice info');
      }
    };
    
    loadProfile();
  }, [profileId, token, apiUrl]);
  
  // Split text into sentences for chunked TTS
  // Merges short sentences together to avoid choppy playback
  // First chunk is larger (20+ words) for smoother start, subsequent chunks are 8+ words
  const splitIntoSentences = useCallback((text) => {
    const MIN_FIRST_CHUNK_WORDS = 20; // First chunk needs more words to avoid gap
    const MIN_WORDS = 8; // Subsequent chunks can be shorter
    
    // Split on sentence endings, keeping the punctuation
    const rawSentences = text.match(/[^.!?]+[.!?]+/g) || [text];
    const sentences = rawSentences.map(s => s.trim()).filter(s => s.length > 2);
    
    if (sentences.length <= 1) {
      return sentences;
    }
    
    // Count words in a string
    const wordCount = (s) => s.split(/\s+/).filter(w => w.length > 0).length;
    
    // Merge short sentences together
    const merged = [];
    let currentChunk = '';
    
    for (let i = 0; i < sentences.length; i++) {
      const sentence = sentences[i];
      
      if (currentChunk === '') {
        currentChunk = sentence;
      } else {
        currentChunk += ' ' + sentence;
      }
      
      // First chunk needs more words, subsequent chunks can be shorter
      const minWords = merged.length === 0 ? MIN_FIRST_CHUNK_WORDS : MIN_WORDS;
      
      // If current chunk has enough words, or it's the last sentence, finalize it
      if (wordCount(currentChunk) >= minWords || i === sentences.length - 1) {
        merged.push(currentChunk);
        currentChunk = '';
      }
    }
    
    // Handle any remaining chunk
    if (currentChunk) {
      // If we have previous chunks and current is too short, merge with last
      if (merged.length > 0 && wordCount(currentChunk) < MIN_WORDS) {
        merged[merged.length - 1] += ' ' + currentChunk;
      } else {
        merged.push(currentChunk);
      }
    }
    
    return merged;
  }, []);
  
  // Play next audio in queue
  const playNextInQueue = useCallback(() => {
    // Don't play if we're leaving the page
    if (isLeavingRef.current) {
      isPlayingQueueRef.current = false;
      setIsSpeaking(false);
      return;
    }
    
    if (audioQueueRef.current.length === 0) {
      isPlayingQueueRef.current = false;
      setIsSpeaking(false);
      // Resume listening after all audio finished
      if (isListeningRef.current) {
        startListening();
      }
      return;
    }
    
    isPlayingQueueRef.current = true;
    const audioUrl = audioQueueRef.current.shift();
    const audio = new Audio(audioUrl);
    currentAudioRef.current = audio;
    
    audio.onended = () => {
      URL.revokeObjectURL(audioUrl);
      currentAudioRef.current = null;
      // Play next chunk
      playNextInQueue();
    };
    
    audio.onerror = (e) => {
      console.error('Audio chunk playback error:', e);
      URL.revokeObjectURL(audioUrl);
      // Try next chunk
      playNextInQueue();
    };
    
    audio.play().catch(e => {
      console.error('Audio play error:', e);
      playNextInQueue();
    });
  }, []);
  
  // Add audio to queue and start playing if not already
  const addToAudioQueue = useCallback((audioUrl) => {
    // Don't add if we're leaving the page
    if (isLeavingRef.current) {
      URL.revokeObjectURL(audioUrl);
      return;
    }
    
    audioQueueRef.current.push(audioUrl);
    
    if (!isPlayingQueueRef.current) {
      playNextInQueue();
    }
  }, [playNextInQueue]);
  
  // Initialize WebSocket connection
  useEffect(() => {
    if (!profileId || !token) return;
    
    const tokenParam = `?token=${encodeURIComponent(token)}`;
    const wsEndpoint = wsUrl
      ? `${wsUrl}/survey/${profileId}${tokenParam}`
      : `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws/survey/${profileId}${tokenParam}`;
    
    console.log('Connecting to survey WebSocket:', wsEndpoint);
    
    const ws = new WebSocket(wsEndpoint);
    wsRef.current = ws;
    
    ws.onopen = () => {
      console.log('Survey WebSocket connected');
      setIsConnected(true);
      setError('');
    };
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('Survey message:', data);
        
        if (data.type === 'session_status') {
          // Handle session status - check if there's a saved session
          if (data.voice_id) {
            voiceIdRef.current = data.voice_id;
            setVoiceId(data.voice_id);
            setHasClonedVoice(true);
          }
          
          if (data.has_saved_session) {
            // Show modal to let user choose to resume or restart
            console.log('📋 Found saved session:', data.session_info);
            setSavedSessionInfo(data.session_info);
            setShowResumeModal(true);
            setWaitingForChoice(true);
          }
          // If no saved session, backend will automatically send first question
          
        } else if (data.type === 'response') {
          // Hide resume modal if showing
          setShowResumeModal(false);
          setWaitingForChoice(false);
          
          // Update voice settings from WebSocket response (first message contains voice info)
          if (data.voice_id !== undefined) {
            if (data.voice_id) {
              console.log('✅ Using cloned voice from WebSocket:', data.voice_id);
              voiceIdRef.current = data.voice_id; // Set ref immediately (sync)
              setVoiceId(data.voice_id); // Also set state for UI
              setHasClonedVoice(true);
            } else if (data.has_cloned_voice === false) {
              console.log('ℹ️ WebSocket confirms no cloned voice, using default');
            }
          }
          
          // Add agent message
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: data.text,
            timestamp: new Date().toISOString()
          }]);
          
          // Update progress and phase
          if (data.progress) {
            setProgress(prev => ({ ...prev, ...data.progress }));
          }
          if (data.phase) {
            setPhase(data.phase);
          }
          if (data.can_finish !== undefined) {
            setCanFinish(data.can_finish);
          }
          if (data.checkpoint) {
            setShowCheckpoint(true);
          } else if (data.phase_transition) {
            setShowCheckpoint(true);
          }
          
          // Check completion
          if (data.is_complete) {
            setIsComplete(true);
          }
          
          // Speak the response (use updated voice settings)
          if (data.text) {
            // Pass voice_id directly if available from this message
            speakTextWithVoice(data.text, data.voice_id);
          }
          
          setIsProcessing(false);
        } else if (data.type === 'survey_complete') {
          setIsComplete(true);
          setMessages(prev => [...prev, {
            role: 'system',
            content: '✅ Survey completed! Your profile has been saved.',
            timestamp: new Date().toISOString()
          }]);
        } else if (data.type === 'error') {
          setError(data.message);
          setIsProcessing(false);
        }
      } catch (e) {
        console.error('Error parsing message:', e);
      }
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setError('Connection error. Please refresh the page.');
      setIsConnected(false);
    };
    
    ws.onclose = () => {
      console.log('WebSocket closed');
      setIsConnected(false);
    };
    
    return () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
  }, [profileId, wsUrl, token]);
  
  // Initialize speech recognition
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setError('Speech recognition not supported. Please use Chrome, Edge, or Safari.');
      return;
    }
    
    const recognition = new SpeechRecognition();
    recognition.continuous = false; // Get complete phrases
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognition.maxAlternatives = 1;
    
    recognition.onstart = () => {
      console.log('🎤 Speech recognition started');
      setIsListening(true);
      isListeningRef.current = true;
    };
    
    recognition.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';
      
      for (let i = 0; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }
      
      // Show interim results
      if (interimTranscript) {
        setCurrentTranscript(interimTranscript);
      }
      
      // Send final results
      if (finalTranscript) {
        console.log('📝 Final transcript:', finalTranscript);
        setCurrentTranscript('');
        sendTranscript(finalTranscript);
      }
    };
    
    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      
      if (event.error === 'not-allowed') {
        setMicPermission('denied');
        setError('Microphone access denied. Please allow microphone access in your browser settings.');
      } else if (event.error === 'no-speech') {
        // No speech detected, restart if still listening
        if (isListeningRef.current) {
          setTimeout(() => {
            try {
              recognition.start();
            } catch (e) {
              console.log('Could not restart recognition');
            }
          }, 100);
        }
      } else if (event.error !== 'aborted') {
        setError(`Speech error: ${event.error}. Please try again.`);
      }
    };
    
    recognition.onend = () => {
      console.log('🎤 Speech recognition ended');
      setIsListening(false);
      
      // Only restart if we're supposed to be listening and not speaking
      if (isListeningRef.current && !isSpeaking) {
        setTimeout(() => {
          try {
            recognition.start();
          } catch (e) {
            console.log('Could not restart recognition:', e);
            isListeningRef.current = false;
          }
        }, 100);
      }
    };
    
    recognitionRef.current = recognition;
    
    return () => {
      recognition.stop();
      isListeningRef.current = false;
    };
  }, [isSpeaking]);
  
  const sendTranscript = useCallback((text) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected');
      return;
    }
    
    // Stop listening while processing
    stopListening();
    setIsProcessing(true);
    
    // Add user message
    setMessages(prev => [...prev, {
      role: 'user',
      content: text,
      timestamp: new Date().toISOString()
    }]);
    
    // Send to server
    wsRef.current.send(JSON.stringify({
      type: 'transcript',
      text: text
    }));
  }, []);
  
  // Handle resume choice - continue from saved session
  const handleResumeSession = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected');
      return;
    }
    
    console.log('📂 Resuming saved session...');
    setShowResumeModal(false);
    setWaitingForChoice(false);
    
    wsRef.current.send(JSON.stringify({
      type: 'resume'
    }));
  }, []);
  
  // Handle restart choice - start fresh
  const handleRestartSession = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected');
      return;
    }
    
    console.log('🔄 Starting new interview...');
    setShowResumeModal(false);
    setWaitingForChoice(false);
    setMessages([]); // Clear any previous messages
    setProgress({ answered: 0, total: 0, core_answered: 0, core_total: 27, adaptive_count: 0 });
    setPhase('core');
    setCanFinish(false);
    setShowCheckpoint(false);
    
    wsRef.current.send(JSON.stringify({
      type: 'restart'
    }));
  }, []);
  
  const getPhaseLabel = () => {
    if (phase === 'adaptive') {
      return `Deep-dive (${progress.adaptive_count || 0} extra)`;
    }
    if (phase === 'finished') {
      return 'Complete';
    }
    return `Essentials (${progress.core_answered || progress.answered}/${progress.core_total || progress.total})`;
  };
  
  const getProgressPercent = () => {
    if (phase === 'adaptive') {
      const coreTotal = progress.core_total || 27;
      return coreTotal > 0 ? 100 : 0;
    }
    const coreAnswered = progress.core_answered ?? progress.answered;
    const coreTotal = progress.core_total || progress.total || 27;
    return coreTotal > 0 ? (coreAnswered / coreTotal) * 100 : 0;
  };
  
  // Fetch TTS for a single chunk (returns audio URL)
  const fetchTTSChunk = useCallback(async (text, voiceToUse) => {
    const requestBody = { text };
    if (voiceToUse) {
      requestBody.voice_id = voiceToUse;
    }
    
    const response = await axios.post(
      `${apiUrl}/tts/speak`,
      requestBody,
      { 
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      }
    );
    
    const audioBlob = new Blob([response.data], { type: 'audio/mpeg' });
    return URL.createObjectURL(audioBlob);
  }, [apiUrl, token]);
  
  // Speak text using ElevenLabs with chunked playback for lower latency
  // Can pass voice_id directly to avoid state timing issues
  const speakTextWithVoice = useCallback(async (text, overrideVoiceId = null) => {
    // Don't start speaking if we're leaving the page
    if (isLeavingRef.current) {
      return;
    }
    
    setIsSpeaking(true);
    
    // Stop listening while speaking
    if (recognitionRef.current && isListeningRef.current) {
      recognitionRef.current.stop();
    }
    
    // Clear any existing audio queue
    audioQueueRef.current = [];
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    isPlayingQueueRef.current = false;
    
    // Use override voice_id if provided, otherwise fall back to ref (sync access)
    const voiceToUse = overrideVoiceId !== null ? overrideVoiceId : voiceIdRef.current;
    
    // Split text into sentences for chunked playback
    const sentences = splitIntoSentences(text);
    console.log('🔊 Chunked TTS:', sentences.length, 'chunks', voiceToUse ? `(cloned voice ${voiceToUse})` : '(default voice)');
    
    if (sentences.length === 0) {
      setIsSpeaking(false);
      setIsPreparingVoice(false);
      return;
    }
    
    // Show preparing indicator while generating first chunk
    setIsPreparingVoice(true);
    
    // If only one sentence, use direct playback (no need for queue)
    if (sentences.length === 1) {
      try {
        const audioUrl = await fetchTTSChunk(sentences[0], voiceToUse);
        setIsPreparingVoice(false); // First chunk ready, hide loading
        addToAudioQueue(audioUrl);
      } catch (err) {
        console.error('TTS chunk error:', err);
        setIsPreparingVoice(false);
        speakWithBrowserTTS(text);
      }
      return;
    }
    
    // Request TTS for all sentences in parallel
    // First chunk is prioritized - wait for it before starting others
    try {
      // Start first chunk immediately
      const firstChunkPromise = fetchTTSChunk(sentences[0], voiceToUse);
      
      // Start remaining chunks in parallel (don't wait for them)
      const remainingPromises = sentences.slice(1).map((sentence, index) => 
        fetchTTSChunk(sentence, voiceToUse)
          .then(audioUrl => ({ index: index + 1, audioUrl }))
          .catch(err => {
            console.error(`TTS chunk ${index + 1} error:`, err);
            return null;
          })
      );
      
      // Wait for first chunk and start playing immediately
      const firstAudioUrl = await firstChunkPromise;
      console.log('🎵 First chunk ready, starting playback');
      setIsPreparingVoice(false); // First chunk ready, hide loading
      addToAudioQueue(firstAudioUrl);
      
      // Collect remaining chunks as they complete (in order)
      const remainingResults = await Promise.all(remainingPromises);
      
      // Add remaining chunks to queue in order
      remainingResults
        .filter(r => r !== null)
        .sort((a, b) => a.index - b.index)
        .forEach(r => {
          console.log(`🎵 Chunk ${r.index + 1} queued`);
          addToAudioQueue(r.audioUrl);
        });
        
    } catch (err) {
      console.error('ElevenLabs TTS error:', err);
      setIsPreparingVoice(false);
      // Fallback to browser TTS if first chunk fails
      speakWithBrowserTTS(text);
    }
  }, [voiceId, fetchTTSChunk, splitIntoSentences, addToAudioQueue]);
  
  // Wrapper for backward compatibility
  const speakText = useCallback((text) => {
    speakTextWithVoice(text, null);
  }, [speakTextWithVoice]);
  
  const speakWithBrowserTTS = useCallback((text) => {
    if (!window.speechSynthesis) {
      setIsSpeaking(false);
      return;
    }
    
    window.speechSynthesis.cancel();
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'en-US';
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    
    utterance.onend = () => {
      setIsSpeaking(false);
      // Resume listening after speaking
      if (isListeningRef.current) {
        startListening();
      }
    };
    
    utterance.onerror = () => {
      setIsSpeaking(false);
    };
    
    window.speechSynthesis.speak(utterance);
  }, []);
  
  const startListening = useCallback(() => {
    if (!recognitionRef.current) {
      setError('Speech recognition not available');
      return;
    }
    
    // Stop any playing audio first
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    // Clear audio queue
    if (audioQueueRef.current) {
      audioQueueRef.current.forEach(url => URL.revokeObjectURL(url));
      audioQueueRef.current = [];
    }
    isPlayingQueueRef.current = false;
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setIsSpeaking(false);
    
    try {
      isListeningRef.current = true;
      recognitionRef.current.start();
      setError('');
    } catch (e) {
      if (e.message.includes('already started')) {
        console.log('Recognition already started');
      } else {
        console.error('Error starting recognition:', e);
        setError('Could not start microphone. Please try again.');
        isListeningRef.current = false;
      }
    }
  }, []);
  
  const stopListening = useCallback(() => {
    isListeningRef.current = false;
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        console.log('Recognition stop error:', e);
      }
    }
    setIsListening(false);
    setCurrentTranscript('');
  }, []);

  const handleFinishInterview = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return;
    }
    isListeningRef.current = false;
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        /* ignore */
      }
    }
    setIsListening(false);
    setIsProcessing(true);
    setShowCheckpoint(false);
    wsRef.current.send(JSON.stringify({ type: 'finish_interview' }));
  }, []);
  
  const stopSpeaking = useCallback(() => {
    // Stop current audio
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    // Clear audio queue and revoke URLs
    if (audioQueueRef.current) {
      audioQueueRef.current.forEach(url => URL.revokeObjectURL(url));
      audioQueueRef.current = [];
    }
    isPlayingQueueRef.current = false;
    // Stop browser TTS
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setIsSpeaking(false);
  }, []);
  
  const handleComplete = useCallback(() => {
    // Stop all audio/recognition before navigating
    stopListening();
    stopSpeaking();
    navigate(`/personalization?survey_complete=${profileId}`);
  }, [navigate, profileId, stopListening, stopSpeaking]);
  
  const requestMicPermission = useCallback(async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      setMicPermission('granted');
      setError('');
    } catch (e) {
      setMicPermission('denied');
      setError('Microphone access denied. Please enable it in browser settings.');
    }
  }, []);
  
  // Handle back navigation - stop all audio before leaving
  const handleBack = useCallback(() => {
    // Mark that we're leaving to prevent new audio from starting
    isLeavingRef.current = true;
    
    // Stop listening and speaking
    stopListening();
    stopSpeaking();
    
    // Also directly stop any playing audio (belt and suspenders)
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.src = '';
      currentAudioRef.current = null;
    }
    
    // Clear the queue
    if (audioQueueRef.current) {
      audioQueueRef.current.forEach(url => URL.revokeObjectURL(url));
      audioQueueRef.current = [];
    }
    
    // Small delay to ensure audio stops before navigation
    setTimeout(() => {
      navigate('/personalization');
    }, 50);
  }, [navigate, stopListening, stopSpeaking]);
  
  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="survey-page">
      {/* Resume/Restart Modal */}
      {showResumeModal && savedSessionInfo && (
        <div className="modal-overlay">
          <div className="modal-content resume-modal">
            <div className="modal-icon">📋</div>
            <h2>Continue Previous Interview?</h2>
            <p className="modal-description">
              You have an interview in progress from a previous session.
            </p>
            
            <div className="session-info-box">
              <div className="session-info-row">
                <span className="info-label">Progress:</span>
                <span className="info-value">
                  {savedSessionInfo.answered_questions} of {savedSessionInfo.total_questions} questions
                  ({savedSessionInfo.progress_percent}%)
                </span>
              </div>
              <div className="session-info-row">
                <span className="info-label">Last activity:</span>
                <span className="info-value">{formatDate(savedSessionInfo.last_activity)}</span>
              </div>
              <div className="progress-bar-mini">
                <div 
                  className="progress-fill"
                  style={{ width: `${savedSessionInfo.progress_percent}%` }}
                />
              </div>
            </div>
            
            <div className="modal-buttons">
              <button 
                className="btn-primary btn-continue"
                onClick={handleResumeSession}
              >
                ▶️ Continue Interview
              </button>
              <button 
                className="btn-secondary btn-restart"
                onClick={handleRestartSession}
              >
                🔄 Start New Interview
              </button>
            </div>
            
            <p className="modal-hint">
              Starting a new interview will discard your previous progress.
            </p>
          </div>
        </div>
      )}
      
      <div className="survey-header">
        <button className="back-button" onClick={handleBack}>
          ← Back
        </button>
        <h1>🎤 Voice Personalization Interview</h1>
        <p>Tell me about yourself so your loved one feels they're really talking to you</p>
        
        <div className={`phase-badge phase-${phase}`}>
          {phase === 'core' && '📋 Phase 1: Essential questions'}
          {phase === 'adaptive' && '✨ Phase 2: Deep-dive (optional — stop anytime)'}
          {phase === 'finished' && '✅ Interview complete'}
        </div>
        
        {/* Progress bar */}
        <div className="progress-container">
          <div 
            className="progress-bar"
            style={{ width: `${getProgressPercent()}%` }}
          />
          <span className="progress-text">
            {getPhaseLabel()}
          </span>
        </div>
      </div>
      
      {/* Status indicators */}
      <div className="status-bar">
        <span className={`status-item ${isConnected ? 'active' : ''}`}>
          {isConnected ? '🟢 Connected' : '🔴 Disconnected'}
        </span>
        <span className={`status-item ${isListening ? 'active listening' : ''}`}>
          {isListening ? '🎙️ Listening...' : '🎙️ Mic Ready'}
        </span>
        <span className={`status-item ${isSpeaking ? 'active speaking' : ''}`}>
          {isSpeaking ? '🔊 Speaking...' : '🔊 Ready'}
        </span>
      </div>
      
      {/* Checkpoint / stop reminder */}
      {(showCheckpoint || (phase === 'adaptive' && canFinish)) && !isComplete && (
        <div className="checkpoint-banner">
          <p>
            {phase === 'adaptive'
              ? 'The deep-dive helps your companion sound more like you. Stop whenever it feels complete — say "I\'m done" or tap Finish below.'
              : 'You can finish after the essential questions if you prefer.'}
          </p>
          {canFinish && (
            <button className="finish-interview-button" onClick={handleFinishInterview} disabled={isProcessing}>
              ✓ Finish Interview
            </button>
          )}
        </div>
      )}
      
      {/* Error message */}
      {error && (
        <div className="error-message">
          ⚠️ {error}
          <button onClick={() => setError('')}>✕</button>
        </div>
      )}
      
      {/* Microphone permission prompt */}
      {micPermission === 'denied' && (
        <div className="permission-prompt">
          <h3>🎤 Microphone Access Required</h3>
          <p>To participate in the voice interview, please allow microphone access.</p>
          <button onClick={requestMicPermission}>Enable Microphone</button>
        </div>
      )}
      
      {/* Messages */}
      <div className="messages-container">
        {messages.length === 0 && !isComplete && (
          <div className="welcome-message">
            <p>👋 Waiting for connection...</p>
            <p>The interview will start automatically once connected.</p>
          </div>
        )}
        
        {messages.map((msg, idx) => (
          <div key={idx} className={`message ${msg.role}`}>
            <div className="message-avatar">
              {msg.role === 'assistant' ? '🤖' : msg.role === 'user' ? '👤' : 'ℹ️'}
            </div>
            <div className="message-content">
              {msg.content}
            </div>
          </div>
        ))}
        
        {/* Current transcript (what you're saying) */}
        {currentTranscript && (
          <div className="message user interim">
            <div className="message-avatar">👤</div>
            <div className="message-content">
              {currentTranscript}
              <span className="typing-indicator">...</span>
            </div>
          </div>
        )}
        
        {/* Processing indicator */}
        {isProcessing && (
          <div className="processing-indicator">
            <span>🤔 Thinking...</span>
          </div>
        )}
        
        {/* Voice preparation indicator */}
        {isPreparingVoice && !isProcessing && (
          <div className="voice-preparing-indicator">
            <div className="hourglass-spinner">⏳</div>
            <span>Preparing voice...</span>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      {/* Controls */}
      <div className="controls">
        {!isComplete ? (
          <div className="control-panel">
            {/* Main action button */}
            <button
              className={`main-action-button ${isListening ? 'listening' : ''} ${isSpeaking ? 'speaking' : ''}`}
              onClick={() => {
                if (isSpeaking) {
                  stopSpeaking();
                } else if (isListening) {
                  stopListening();
                } else {
                  startListening();
                }
              }}
              disabled={!isConnected || isProcessing || micPermission === 'denied'}
            >
              {isSpeaking ? (
                <>
                  <span className="button-icon">⏹️</span>
                  <span className="button-text">Skip (Stop Speaking)</span>
                </>
              ) : isListening ? (
                <>
                  <span className="button-icon pulse">🎙️</span>
                  <span className="button-text">Listening... (tap to stop)</span>
                </>
              ) : (
                <>
                  <span className="button-icon">🎤</span>
                  <span className="button-text">Tap to Speak</span>
                </>
              )}
            </button>
            
            <p className="hint">
              {isSpeaking 
                ? "Tap to skip the current response" 
                : isListening 
                  ? "Speak your answer clearly, then tap when done"
                  : "Tap the button and speak your answer"}
            </p>
            
            {/* Voice indicator */}
            <div className="voice-indicator">
              {hasClonedVoice 
                ? '🎵 Using your cloned voice' 
                : '🎵 Using ElevenLabs voice'}
            </div>
          </div>
        ) : (
          <div className="completion-section">
            <div className="completion-icon">🎉</div>
            <h2>Interview Complete!</h2>
            <p>Your personalization profile has been created successfully.</p>
            <p className="next-step">
              {hasClonedVoice 
                ? "Your voice is already set up! You're ready to go."
                : "Next step: Upload a voice sample to complete your profile."}
            </p>
            <button className="complete-button" onClick={handleComplete}>
              {hasClonedVoice ? 'View Profile →' : 'Continue to Voice Setup →'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SurveyPage;
